import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Syringe, Pill, User, AlertTriangle, Clock, Activity, CalendarPlus, ClipboardList, TrendingUp, TrendingDown, Dog, Cat, Stethoscope } from 'lucide-react';
import AnimalDetail from '../components/AnimalDetail';
import TratamentosExternosPage from './TratamentosExternosPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const placeholderImg = '/sem-foto.jpg';
const BEHAVIOR_COLORS = { verde: '#4CAF50', amarelo: '#FFC107', vermelho: '#F44336' };
const fmtDate = (d) => { if (!d) return 'N/D'; const p = d.slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
const VACCINE_OPTIONS = ['Múltipla Cães', 'Raiva', 'Múltipla+Raiva', 'Tripla Gatos', 'Puppy'];
const DEWORMING_PRODUCTS = ['Bravecto', 'Zipyran', 'Bravecto+Zipyran', 'FrontLine', 'Stronghold', 'Outra'];
const PER_PAGE = 16;

function DashboardSummary() {
  const [resumo, setResumo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dashboard/resumo`).then(r => setResumo(r.data)).catch(() => {});
  }, []);

  if (!resumo) return null;

  const hasUrgent = resumo.vacinas_atrasadas > 0 || resumo.pedidos_pendentes > 0;
  const fmtD = (d) => { if (!d) return ''; const p = d.slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };

  return (
    <div className="mb-8 space-y-4" data-testid="dashboard-summary">
      {/* Quick stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-7 gap-3">
        <QuickStat icon={<Dog className="w-4 h-4" />} value={resumo.caes} label="Cães" color="text-blue-600" bg="bg-blue-50" />
        <QuickStat icon={<Cat className="w-4 h-4" />} value={resumo.gatos} label="Gatos" color="text-indigo-600" bg="bg-indigo-50" />
        <QuickStat icon={<TrendingUp className="w-4 h-4" />} value={resumo.novos_semana} label="Novos (7d)" color="text-green-600" bg="bg-green-50" />
        <QuickStat icon={<TrendingDown className="w-4 h-4" />} value={resumo.saidas_semana} label="Saídas (7d)" color="text-gray-600" bg="bg-gray-50" />
        <QuickStat icon={<Activity className="w-4 h-4" />} value={resumo.tratamentos_ativos} label="Trat. Ativos" color="text-purple-600" bg="bg-purple-50" />
        <QuickStat icon={<Stethoscope className="w-4 h-4" />} value={resumo.tratamentos_externos_total} label="Trat. Externos" color="text-teal-600" bg="bg-teal-50" />
        <QuickStat icon={<ClipboardList className="w-4 h-4" />} value={resumo.pedidos_pendentes} label="Pedidos" color="text-amber-600" bg="bg-amber-50" />
      </div>

      {/* Alerts row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Overdue vaccines */}
        <Link to="/calendario" data-testid="dashboard-vacinas-atrasadas" className={`rounded-2xl border p-4 transition-all hover:shadow-md ${resumo.vacinas_atrasadas > 0 ? 'bg-red-50 border-red-200 hover:border-red-400' : 'bg-card border-border hover:border-primary/40'}`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className={`w-4 h-4 ${resumo.vacinas_atrasadas > 0 ? 'text-red-600' : 'text-muted-foreground'}`} />
            <h3 className="font-heading font-bold text-sm">Vacinas Atrasadas</h3>
            <span className={`ml-auto text-lg font-black ${resumo.vacinas_atrasadas > 0 ? 'text-red-600' : 'text-green-600'}`}>{resumo.vacinas_atrasadas}</span>
          </div>
          {resumo.vacinas_atrasadas_lista.length > 0 ? (
            <div className="space-y-1">
              {resumo.vacinas_atrasadas_lista.map((v, i) => (
                <p key={i} className="text-xs text-foreground truncate">
                  <strong>{v.animal?.nome}</strong> — {v.vacina} <span className="text-red-500">(desde {fmtD(v.data)})</span>
                </p>
              ))}
              {resumo.vacinas_atrasadas > 5 && <span className="text-xs text-primary hover:underline">Ver todas →</span>}
            </div>
          ) : <p className="text-xs text-green-600">Tudo em dia!</p>}
        </Link>

        {/* Upcoming vaccines */}
        <div className="bg-card rounded-2xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-accent" />
            <h3 className="font-heading font-bold text-sm">Vacinas Próximas (7d)</h3>
            <span className="ml-auto text-lg font-black text-accent">{resumo.vacinas_proximas}</span>
          </div>
          {resumo.vacinas_proximas_lista.length > 0 ? (
            <div className="space-y-1">
              {resumo.vacinas_proximas_lista.map((v, i) => (
                <p key={i} className="text-xs text-foreground truncate">
                  <strong>{v.animal?.nome}</strong> — {v.vacina} <span className="text-muted-foreground">({fmtD(v.data)})</span>
                </p>
              ))}
            </div>
          ) : <p className="text-xs text-muted-foreground">Sem vacinas agendadas.</p>}
        </div>

        {/* Active treatments */}
        <div className="bg-card rounded-2xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Pill className="w-4 h-4 text-purple-600" />
            <h3 className="font-heading font-bold text-sm">Tratamentos Ativos</h3>
            <span className="ml-auto text-lg font-black text-purple-600">{resumo.tratamentos_ativos}</span>
          </div>
          {resumo.tratamentos_lista.length > 0 ? (
            <div className="space-y-1">
              {resumo.tratamentos_lista.map((t, i) => (
                <p key={i} className="text-xs text-foreground truncate">
                  <strong>{t.animal?.nome}</strong> — {t.nome} <span className="text-muted-foreground">(início: {fmtD(t.data_inicio)})</span>
                </p>
              ))}
              {resumo.tratamentos_ativos > 5 && <Link to="/tratamentos" className="text-xs text-primary hover:underline">Ver todos →</Link>}
            </div>
          ) : <p className="text-xs text-muted-foreground">Nenhum tratamento ativo.</p>}
        </div>
      </div>
    </div>
  );
}

function QuickStat({ icon, value, label, color, bg }) {
  return (
    <div className={`${bg} rounded-xl p-3 flex items-center gap-2.5`}>
      <div className={`${color}`}>{icon}</div>
      <div>
        <p className={`text-xl font-heading font-black ${color}`}>{value}</p>
        <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [animais, setAnimais] = useState([]);
  const [pesquisa, setPesquisa] = useState('');
  const [especie, setEspecie] = useState('todos');
  const [pagina, setPagina] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showMassVac, setShowMassVac] = useState(false);
  const [showMassDesp, setShowMassDesp] = useState(false);
  const [massVacForm, setMassVacForm] = useState({ nome: VACCINE_OPTIONS[0], data: '', proxima_data: '', observacao: '', animal_ids: [] });
  const [massDespForm, setMassDespForm] = useState({ produto: DEWORMING_PRODUCTS[0], data: '', observacao: '', animal_ids: [] });
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [tipoVista, setTipoVista] = useState('cro'); // 'cro' | 'sequestro_domiciliario'
  const [esterilizacao, setEsterilizacao] = useState('todos'); // 'todos' | 'sim' | 'nao'
  const [comportamentoF, setComportamentoF] = useState('todos');
  const [perfisComp, setPerfisComp] = useState([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const carregar = useCallback(() => {
    const params = new URLSearchParams();
    if (pesquisa.trim()) params.set('pesquisa', pesquisa.trim());
    if (especie !== 'todos') params.set('especie', especie);
    if (esterilizacao !== 'todos') params.set('esterilizado', esterilizacao === 'sim' ? 'true' : 'false');
    if (comportamentoF !== 'todos') params.set('comportamento', comportamentoF);
    params.set('ativos', 'true');
    params.set('tipo_registo', tipoVista);
    axios.get(`${API}/animais?${params}`).then(r => setAnimais(r.data)).catch(console.error);
    axios.get(`${API}/animais/perfis-comportamento`).then(r => setPerfisComp(r.data)).catch(() => {});
  }, [pesquisa, especie, esterilizacao, comportamentoF, tipoVista]);

  useEffect(() => { carregar(); }, [carregar]);

  // Open animal detail when ?animal=<id> query param is set (e.g. coming from calendar)
  useEffect(() => {
    const animalId = searchParams.get('animal');
    if (!animalId) return;
    axios.get(`${API}/animais/${animalId}`)
      .then(r => { setSelected(r.data); setShowDetail(true); })
      .catch(() => {})
      .finally(() => {
        const next = new URLSearchParams(searchParams);
        next.delete('animal');
        setSearchParams(next, { replace: true });
      });
  }, [searchParams, setSearchParams]);

  const total = Math.ceil(animais.length / PER_PAGE);
  const visiveis = animais.slice(pagina * PER_PAGE, (pagina + 1) * PER_PAGE);

  const abrirFicha = (a) => { if (selectMode) { toggleSelect(a.id); return; } setSelected(a); setShowDetail(true); };
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAll = () => setSelectedIds(animais.map(a => a.id));

  const executarMassVac = async () => {
    await axios.post(`${API}/vacinas/massa`, { ...massVacForm, animal_ids: selectedIds });
    alert(`Vacinação registada para ${selectedIds.length} animais!`);
    setShowMassVac(false); setSelectMode(false); setSelectedIds([]); carregar();
  };

  const executarMassDesp = async () => {
    await axios.post(`${API}/desparasitacoes/massa`, { ...massDespForm, animal_ids: selectedIds });
    alert(`Desparasitação registada para ${selectedIds.length} animais!`);
    setShowMassDesp(false); setSelectMode(false); setSelectedIds([]); carregar();
  };

  return (
    <div data-testid="dashboard-page">
      {/* Dashboard Summary */}
      <DashboardSummary />

      {/* Animal list */}
      <div className="flex flex-wrap gap-3 items-center mb-3">
        <h2 className="font-heading font-bold text-2xl text-foreground mr-2">{tipoVista === 'sequestro_domiciliario' ? 'Sequestros Domiciliários' : tipoVista === 'tratamento_externo' ? 'Tratamentos Externos' : 'Animais no Canil'}</h2>
        <div className="inline-flex bg-muted rounded-full p-1 border border-border" role="tablist" data-testid="tipo-vista-toggle">
          <button onClick={() => { setTipoVista('cro'); setPagina(0); }} data-testid="tipo-cro"
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all ${tipoVista === 'cro' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            CRO
          </button>
          <button onClick={() => { setTipoVista('sequestro_domiciliario'); setPagina(0); }} data-testid="tipo-seq-dom"
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all ${tipoVista === 'sequestro_domiciliario' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            Seq. Domiciliário
          </button>
          <button onClick={() => { setTipoVista('tratamento_externo'); setPagina(0); }} data-testid="tipo-trat-ext"
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all ${tipoVista === 'tratamento_externo' ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            Tratamentos Externos
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 items-end mb-6">
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5 ml-0.5">Pesquisa</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input data-testid="search-input" value={pesquisa} onChange={e => { setPesquisa(e.target.value); setPagina(0); }}
              placeholder="Chip, nome, processo ou box" className="pl-9 pr-3 py-2 rounded-xl border border-border bg-card text-sm w-56 focus:ring-2 focus:ring-primary focus:outline-none" />
          </div>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5 ml-0.5">Espécie</label>
          <select data-testid="species-filter" value={especie} onChange={e => { setEspecie(e.target.value); setPagina(0); }}
            className="rounded-xl border border-border bg-card px-3 py-2 text-sm h-[38px]">
            <option value="todos">Todas</option><option value="canina">Canina</option><option value="felina">Felina</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5 ml-0.5">Esterilização</label>
          <select data-testid="esterilizacao-filter" value={esterilizacao} onChange={e => { setEsterilizacao(e.target.value); setPagina(0); }}
            className="rounded-xl border border-border bg-card px-3 py-2 text-sm h-[38px]">
            <option value="todos">Todos</option>
            <option value="sim">Esterilizados</option>
            <option value="nao">Não esterilizados</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5 ml-0.5">Comportamento</label>
          <select data-testid="comportamento-filter" value={comportamentoF} onChange={e => { setComportamentoF(e.target.value); setPagina(0); }}
            className="rounded-xl border border-border bg-card px-3 py-2 text-sm h-[38px] min-w-[120px]">
            <option value="todos">Todos</option>
            {perfisComp.map(p => <option key={p.id} value={p.nome}>{p.nome}</option>)}
          </select>
        </div>
        <div className="flex gap-1 print:hidden items-center h-[38px]">
          <button onClick={() => { setSelectMode(!selectMode); setSelectedIds([]); setShowMassVac(false); setShowMassDesp(false); }}
            className={`text-xs rounded-xl px-3 h-[38px] flex items-center gap-1 transition-all border ${selectMode ? 'bg-accent text-accent-foreground border-accent' : 'bg-card text-foreground border-border hover:bg-muted'}`}>
            {selectMode ? 'Cancelar Seleção' : 'Ações em Massa'}
          </button>
          {selectMode && selectedIds.length > 0 && (<>
            <button onClick={() => setShowMassVac(true)} className="text-xs bg-primary text-primary-foreground rounded-xl px-3 h-[38px] flex items-center gap-1"><Syringe className="w-3 h-3" />Vacinar ({selectedIds.length})</button>
            <button onClick={() => setShowMassDesp(true)} className="text-xs bg-green-600 text-white rounded-xl px-3 h-[38px] flex items-center gap-1"><Pill className="w-3 h-3" />Desparasitar ({selectedIds.length})</button>
          </>)}
          {selectMode && <button onClick={selectAll} className="text-xs bg-muted text-foreground rounded-xl px-2 h-[38px]">Selec. Todos</button>}
        </div>
      </div>

      {tipoVista === 'tratamento_externo' ? (
        <TratamentosExternosPage />
      ) : visiveis.length === 0 ? (
        <p className="text-muted-foreground text-center py-12">Nenhum animal encontrado.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {visiveis.map(a => {
            const fotoUrl = a.fotos?.length > 0 ? ((u) => (u && (u.startsWith('http') || u.startsWith('data:'))) ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`)(a.fotos[a.foto_capa_index || 0]) : placeholderImg;
            const isSelected = selectedIds.includes(a.id);
            return (
              <div key={a.id} data-testid={`animal-card-${a.id}`} onClick={() => abrirFicha(a)}
                className={`bg-card rounded-2xl border overflow-hidden cursor-pointer hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group ${isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-border'}`}>
                <div className="relative h-44 overflow-hidden">
                  <img src={fotoUrl} alt={a.nome} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={e => { e.target.src = placeholderImg; }} />
                  {a.cor_comportamento && <span className="absolute top-2 right-2 w-4 h-4 rounded-full border-2 border-white shadow" style={{ backgroundColor: BEHAVIOR_COLORS[a.cor_comportamento] || '#4CAF50' }} />}
                  {a.titular && <span className={`absolute top-2 left-2 ${a.data_transferencia_cro ? 'bg-green-600' : 'bg-blue-600'} text-white text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5`}><User className="w-2.5 h-2.5" />Titular</span>}
                  {a.tribunal && <span className={`absolute ${a.titular ? 'top-8' : 'top-2'} left-2 bg-red-600 text-white text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5`} data-testid={`badge-tribunal-${a.id}`}><AlertTriangle className="w-2.5 h-2.5" />Tribunal</span>}
                  {selectMode && <div className={`absolute inset-0 ${isSelected ? 'bg-primary/20' : 'bg-transparent'}`}><div className={`absolute top-2 right-8 w-5 h-5 rounded-full border-2 ${isSelected ? 'bg-primary border-primary' : 'bg-white/80 border-border'}`} /></div>}
                </div>
                <div className="p-3">
                  <h3 className="font-heading font-bold text-foreground truncate">{a.nome}</h3>
                  <p className="text-xs text-muted-foreground capitalize">{a.especie} | {a.sexo || 'N/D'}</p>
                  {a.chip && (
                    <p className="mt-1 font-bold text-sm text-primary tracking-wider" data-testid={`card-chip-${a.id}`}>
                      Chip: {a.chip}
                    </p>
                  )}
                  <div className="mt-1.5 text-xs text-muted-foreground space-y-0.5">
                    <p><strong>Box:</strong> {a.box || 'N/D'}</p>
                    <p><strong>Entrada:</strong> {fmtDate(a.entrada)}</p>
                    <p className={a.esterilizado ? 'text-green-700' : 'text-amber-700'}>
                      <strong>Esterilizado:</strong> {a.esterilizado ? 'Sim ✓' : 'Não'}
                    </p>
                    {a.data_desparasitacao && <p><strong>Últ. Despar.:</strong> {fmtDate(a.data_desparasitacao)}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {total > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button onClick={() => setPagina(p => Math.max(0, p - 1))} disabled={pagina === 0} className="p-2 rounded-full border border-border bg-card hover:bg-muted disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-muted-foreground">Página {pagina + 1} de {total}</span>
          <button onClick={() => setPagina(p => Math.min(total - 1, p + 1))} disabled={pagina >= total - 1} className="p-2 rounded-full border border-border bg-card hover:bg-muted disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}

      {showDetail && selected && <AnimalDetail animal={selected} onClose={() => { setShowDetail(false); setSelected(null); }} onUpdate={carregar} onEdit={(id) => navigate(`/editar/${id}`)} />}

      {/* Mass Vaccination Modal */}
      {showMassVac && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm m-4 space-y-3">
            <h3 className="font-heading font-bold text-lg">Vacinação em Massa ({selectedIds.length} animais)</h3>
            <select value={massVacForm.nome} onChange={e => setMassVacForm({ ...massVacForm, nome: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm">
              {VACCINE_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs">Data</label><input type="date" value={massVacForm.data} onChange={e => setMassVacForm({ ...massVacForm, data: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
              <div><label className="text-xs">Próxima</label><input type="date" value={massVacForm.proxima_data} onChange={e => setMassVacForm({ ...massVacForm, proxima_data: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" /></div>
            </div>
            <div>
              <label className="text-xs">Observações</label>
              <textarea data-testid="mass-vac-obs" rows={2} value={massVacForm.observacao}
                onChange={e => setMassVacForm({ ...massVacForm, observacao: e.target.value })}
                placeholder="ex: lote, reação, marca…"
                className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowMassVac(false)} className="text-xs bg-muted text-foreground rounded-full px-3 py-1.5">Cancelar</button>
              <button onClick={executarMassVac} className="text-xs bg-primary text-primary-foreground rounded-full px-3 py-1.5">Vacinar</button>
            </div>
          </div>
        </div>
      )}

      {/* Mass Deworming Modal */}
      {showMassDesp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-card rounded-2xl border border-border p-6 w-full max-w-sm m-4 space-y-3">
            <h3 className="font-heading font-bold text-lg">Desparasitação em Massa ({selectedIds.length} animais)</h3>
            <select value={massDespForm.produto} onChange={e => setMassDespForm({ ...massDespForm, produto: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm">
              {DEWORMING_PRODUCTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <input type="date" value={massDespForm.data} onChange={e => setMassDespForm({ ...massDespForm, data: e.target.value })} className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
            <div>
              <label className="text-xs">Observações</label>
              <textarea data-testid="mass-desp-obs" rows={2} value={massDespForm.observacao}
                onChange={e => setMassDespForm({ ...massDespForm, observacao: e.target.value })}
                placeholder="ex: lote, dose, reação…"
                className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowMassDesp(false)} className="text-xs bg-muted text-foreground rounded-full px-3 py-1.5">Cancelar</button>
              <button onClick={executarMassDesp} className="text-xs bg-green-600 text-white rounded-full px-3 py-1.5">Desparasitar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
